import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { useIsElectron, useElectronAPI, useIsLoading } from './ElectronContext';
import { useBridgeRequired } from './BridgeRequiredContext';
import { desktopBridge } from '../services/desktopBridge';

interface ProjectDirectoryInfo {
  projectId: string;
  directoryName: string;
  lastUsed: number;
  handle?: FileSystemDirectoryHandle;
  // For Electron: store the actual file path
  filePath?: string;
}

interface ProjectDirectoriesContextType {
  getProjectDirectory: (projectId: string) => FileSystemDirectoryHandle | null;
  getStoredProjectPath: (projectId: string) => string | null;
  setProjectDirectory: (
    projectId: string,
    handle: FileSystemDirectoryHandle,
    filePath?: string
  ) => Promise<void>;
  removeProjectDirectory: (projectId: string) => void;
  getAllProjectDirectories: () => Record<string, ProjectDirectoryInfo>;
  requestStoredDirectoryAccess: (
    projectId: string
  ) => Promise<FileSystemDirectoryHandle | null>;
  hasStoredProjectInfo: (projectId: string) => boolean;
  getStoredProjectInfo: (projectId: string) => ProjectDirectoryInfo | null;
}

const ProjectDirectoriesContext = createContext<
  ProjectDirectoriesContextType | undefined
>(undefined);

interface ProjectDirectoriesProviderProps {
  children: ReactNode;
}

export const ProjectDirectoriesProvider: React.FC<
  ProjectDirectoriesProviderProps
> = ({ children }) => {
  const [projectDirectories, setProjectDirectories] = useState<
    Record<string, ProjectDirectoryInfo>
  >({});
  const [isLoaded, setIsLoaded] = useState(false);

  const isElectron = useIsElectron();
  const electronAPI = useElectronAPI();
  const isLoading = useIsLoading();
  const { requireBridge } = useBridgeRequired();

  // Persistent storage functions for Electron
  const saveProjectDirectoriesToFile = useCallback(async () => {
    if (!isElectron || !electronAPI) return;

    try {
      const storageData = {
        projectDirectories: Object.fromEntries(
          Object.entries(projectDirectories).map(([projectId, info]) => [
            projectId,
            {
              projectId: info.projectId,
              directoryName: info.directoryName,
              lastUsed: info.lastUsed,
              filePath: info.filePath, // Store the file path
              // Don't store the handle, just the metadata
            },
          ])
        ),
        lastUpdated: Date.now(),
      };

      // Save to user's app data directory
      let storagePath: string;

      try {
        if (electronAPI.getUserDataPath) {
          const userDataPath = await electronAPI.getUserDataPath();
          storagePath = `${userDataPath}/project-directories.json`;
        } else {
          // Fallback to current directory
          storagePath = 'project-directories.json';
        }
      } catch (error) {
        console.warn('Failed to get user data path, using fallback:', error);
        storagePath = 'project-directories.json';
      }

      await electronAPI.writeFile(
        storagePath,
        JSON.stringify(storageData, null, 2)
      );
    } catch (error) {
      console.error('Failed to save project directories:', error);
    }
  }, [isElectron, electronAPI, projectDirectories]);

  const loadProjectDirectoriesFromFile = useCallback(async () => {
    if (isElectron && electronAPI) {
      // Electron: Load from local file
      try {
        // Load from user's app data directory
        let storagePath: string;

        try {
          if (electronAPI.getUserDataPath) {
            const userDataPath = await electronAPI.getUserDataPath();
            storagePath = `${userDataPath}/project-directories.json`;
          } else {
            // Fallback to current directory
            storagePath = 'project-directories.json';
          }
        } catch (error) {
          console.warn('Failed to get user data path, using fallback:', error);
          storagePath = 'project-directories.json';
        }

        const result = await electronAPI.readFile(storagePath);
        if (result.success && result.content) {
          const storageData = JSON.parse(result.content);
          const loadedDirectories: Record<string, ProjectDirectoryInfo> = {};

          // Convert stored data back to ProjectDirectoryInfo format
          Object.entries(storageData.projectDirectories || {}).forEach(
            ([projectId, info]) => {
              const projectInfo = info as {
                projectId: string;
                directoryName: string;
                lastUsed: number;
                filePath?: string;
              };
              loadedDirectories[projectId] = {
                projectId: projectInfo.projectId,
                directoryName: projectInfo.directoryName,
                lastUsed: projectInfo.lastUsed,
                filePath: projectInfo.filePath, // Restore the file path
                // handle will be null until user re-selects the directory
              };
            }
          );

          // A user can select a directory while the persistence read is still
          // in flight. Preserve those newer in-memory choices when hydrating.
          setProjectDirectories((currentDirectories) => ({
            ...loadedDirectories,
            ...currentDirectories,
          }));
        }
      } catch (error) {
        // Silently handle missing project directories file
      } finally {
        setIsLoaded(true);
      }
    } else {
      // Web: Load from desktop app via bridge
      try {
        const isDesktopAvailable = await desktopBridge.isDesktopAvailable();
        if (isDesktopAvailable) {
          const result = await desktopBridge.getStoredProjectDirectories();
          if (result.success && result.projectDirectories) {
            const loadedDirectories: Record<string, ProjectDirectoryInfo> = {};

            // Convert stored data back to ProjectDirectoryInfo format
            Object.entries(result.projectDirectories).forEach(
              ([projectId, info]) => {
                loadedDirectories[projectId] = {
                  projectId: info.projectId,
                  directoryName: info.directoryName,
                  lastUsed: info.lastUsed,
                  filePath: info.filePath, // Restore the file path
                  // handle will be null until user re-selects the directory
                };
              }
            );

            setProjectDirectories((currentDirectories) => ({
              ...loadedDirectories,
              ...currentDirectories,
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load project directories from bridge:', error);
      } finally {
        setIsLoaded(true);
      }
    }
  }, [isElectron, electronAPI]);

  // Load stored directories on mount (Electron or web with desktop bridge)
  useEffect(() => {
    // Wait for Electron context to finish loading (if in Electron)
    if (isElectron && isLoading) return;

    if (!isLoaded) {
      loadProjectDirectoriesFromFile();
    }
  }, [
    isElectron,
    isLoaded,
    isLoading,
    electronAPI,
    loadProjectDirectoriesFromFile,
  ]);

  // Save directories when they change (Electron only)
  useEffect(() => {
    if (isElectron && isLoaded) {
      saveProjectDirectoriesToFile();
    }
  }, [projectDirectories, isElectron, isLoaded, saveProjectDirectoriesToFile]);

  // Recreate handles for Electron paths that don't have handles yet
  useEffect(() => {
    if (!isElectron || !electronAPI || !isLoaded) return;

    // Check if any updates are needed
    const needsUpdate = Object.values(projectDirectories).some(
      (projectInfo) => projectInfo?.filePath && !projectInfo.handle
    );

    if (!needsUpdate) return;

    // Only update directories that need handles
    const updatedDirectories = { ...projectDirectories };
    Object.entries(projectDirectories).forEach(([projectId, projectInfo]) => {
      if (projectInfo?.filePath && !projectInfo.handle) {
        const mockHandle = {
          name: projectInfo.filePath.split(/[/\\]/).pop() || 'Project',
          kind: 'directory' as const,
        } as FileSystemDirectoryHandle;

        updatedDirectories[projectId] = {
          ...projectInfo,
          handle: mockHandle,
        };
      }
    });

    setProjectDirectories(updatedDirectories);
  }, [projectDirectories, isElectron, electronAPI, isLoaded]);

  const getProjectDirectory = useCallback(
    (projectId: string): FileSystemDirectoryHandle | null => {
      const projectInfo = projectDirectories[projectId];

      if (projectInfo?.handle) {
        return projectInfo.handle;
      }

      // If we have a stored path but no handle, return a temporary mock handle
      // The useEffect above will update the state with the handle
      if (isElectron && projectInfo?.filePath && electronAPI) {
        return {
          name: projectInfo.filePath.split(/[/\\]/).pop() || 'Project',
          kind: 'directory' as const,
        } as FileSystemDirectoryHandle;
      }

      return null;
    },
    [projectDirectories, isElectron, electronAPI]
  );

  const setProjectDirectory = useCallback(
    async (
      projectId: string,
      handle: FileSystemDirectoryHandle,
      filePath?: string
    ): Promise<void> => {
      const directoryInfo: ProjectDirectoryInfo = {
        projectId,
        directoryName: handle.name,
        lastUsed: Date.now(),
        handle,
        filePath, // Store the file path if provided
      };

      setProjectDirectories((currentDirectories) => ({
        ...currentDirectories,
        [projectId]: directoryInfo,
      }));
    },
    []
  );

  const removeProjectDirectory = useCallback((projectId: string): void => {
    setProjectDirectories((currentDirectories) => {
      const updatedDirectories = { ...currentDirectories };
      delete updatedDirectories[projectId];
      return updatedDirectories;
    });
  }, []);

  const getAllProjectDirectories = useCallback((): Record<
    string,
    ProjectDirectoryInfo
  > => {
    return projectDirectories;
  }, [projectDirectories]);

  const requestStoredDirectoryAccess = useCallback(
    async (projectId: string): Promise<FileSystemDirectoryHandle | null> => {
      // Check if we already have a directory for this project in the current session
      const existingDirectory = projectDirectories[projectId];
      if (existingDirectory?.handle) {
        return existingDirectory.handle;
      }

      try {
        if (isElectron && electronAPI) {
          // Use Electron's folder picker
          const folderPath = await electronAPI.selectFolder();
          if (folderPath) {
            // For Electron, we'll store the path and create a mock handle
            // The actual file operations will use the path directly
            const mockHandle = {
              name: folderPath.split(/[/\\]/).pop() || 'Project',
              kind: 'directory' as const,
            } as FileSystemDirectoryHandle;

            await setProjectDirectory(projectId, mockHandle, folderPath);

            return mockHandle;
          }
        } else {
          // Use desktop bridge; if unavailable the DesktopAppRequiredDialog
          // will guide the user — do not fall back to the browser picker
          // here because subsequent sync operations require the bridge too.
          if (!(await requireBridge('selectFolder'))) {
            return null;
          }
          const folderPath = await desktopBridge.selectFolder();
          if (folderPath) {
            // Create a mock handle for the desktop-selected folder
            const mockHandle = {
              name: folderPath.split(/[/\\]/).pop() || 'Project',
              kind: 'directory' as const,
            } as FileSystemDirectoryHandle;

            // Store the directory info using setProjectDirectory
            await setProjectDirectory(projectId, mockHandle, folderPath);

            return mockHandle;
          }
        }

        return null;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled
          return null;
        }
        console.error(
          `Failed to get directory access for project ${projectId}:`,
          error
        );
        return null;
      }
    },
    [
      projectDirectories,
      setProjectDirectory,
      isElectron,
      electronAPI,
      requireBridge,
    ]
  );

  const hasStoredProjectInfo = useCallback(
    (projectId: string): boolean => {
      return projectId in projectDirectories;
    },
    [projectDirectories]
  );

  const getStoredProjectInfo = useCallback(
    (projectId: string): ProjectDirectoryInfo | null => {
      return projectDirectories[projectId] || null;
    },
    [projectDirectories]
  );

  // Get the stored project path (returns the full path)
  // Works for both Electron and web (when desktop app is connected)
  const getStoredProjectPath = useCallback(
    (projectId: string): string | null => {
      const storedInfo = projectDirectories[projectId];
      if (storedInfo && storedInfo.filePath) {
        return storedInfo.filePath; // Return the actual stored file path
      }
      return null;
    },
    [projectDirectories]
  );

  const value = {
    getProjectDirectory,
    getStoredProjectPath,
    setProjectDirectory,
    removeProjectDirectory,
    getAllProjectDirectories,
    requestStoredDirectoryAccess,
    hasStoredProjectInfo,
    getStoredProjectInfo,
  };

  return (
    <ProjectDirectoriesContext.Provider value={value}>
      {children}
    </ProjectDirectoriesContext.Provider>
  );
};

export const useProjectDirectories = (): ProjectDirectoriesContextType => {
  const context = useContext(ProjectDirectoriesContext);
  if (context === undefined) {
    throw new Error(
      'useProjectDirectories must be used within a ProjectDirectoriesProvider'
    );
  }
  return context;
};
